//Built-in libraries
import type { ChangeEvent } from 'react';

//Third party libraries

//Custom components
import Button from '@/components/UI/Button';
import Input from '@/components/UI/Form/Input';
import ReactSelect from '@/components/UI/Form/ReactSelect';
import Modal, { type ModalProps } from '@/components/UI/Modal';

//Services

import { XCircleIcon } from '@heroicons/react/24/outline';
import InputError from '@/components/UI/Form/Error';
import FileInput from '@/components/UI/Form/FileInput';
//Assets andd Miscellaneous
import type { ReactSelectOption } from '@/types/Constants';
import { relation } from './constants';

//Component Begin

interface IAddDevelopmentTeamMemberFormProps extends ModalProps {
  modalHandler: () => void;
  income: { [key: string]: { [key: string]: any } };
  inputChangedHandler: (value: string | number | boolean | ChangeEvent<HTMLInputElement> | undefined, name: string) => void;
  onAddIncomeHandler: () => void;
  isEditing: boolean;
  updateIncomeHandler: () => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement | null>) => void;
  removeFileHandler: (name: string) => void;
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  selectedFiles: File[];
  uploadError: string;
  setUploadError: React.Dispatch<React.SetStateAction<string>>;
}

export default function AddHouseholdIncomeForm({
  modalHandler,
  isEditing,
  income,
  inputChangedHandler,
  onAddIncomeHandler,
  updateIncomeHandler,
  handleFileChange,
  removeFileHandler,
  setSelectedFiles,
  selectedFiles,
  uploadError,
  setUploadError,
  ...rest
}: IAddDevelopmentTeamMemberFormProps) {
  return (
    <Modal width='min-w-1/2'>
      <h2 className='mt-0'>{`${isEditing ? 'Edit' : 'Add New'} Family Member`}</h2>
      <div>
        <section className='space-y-4'>
          <div className='grid grid-cols-3 gap-5'>
            <Input
              label='Family Member Name'
              id='family-member-name'
              name='familyMemberName'
              type='text'
              value={income?.familyMemberName.value}
              error={income?.familyMemberName?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
            />
            <Input
              label='Age'
              id='age'
              name='age'
              type='number'
              value={income?.age.value}
              error={income?.age?.error}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
              onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                inputChangedHandler(e.target.value, e.target.name);
              }}
            />
            <div>
              <ReactSelect
                label='Relation'
                id='relation'
                name='relation'
                options={relation.map((dt) => ({
                  label: dt,
                  value: dt,
                }))}
                value={{
                  label: income?.relation.value,
                  value: income?.relation.value,
                }}
                placeholder='Select'
                error={income?.relation?.error}
                onChange={(option) => {
                  inputChangedHandler((option as ReactSelectOption).value, 'relation');
                }}
              />
            </div>
            <h4 className='col-span-6'>Please mention the Income Sources</h4>
            <div className='relative'>
              <span className='absolute inset-y-12 left-1 flex items-center pointer-events-none'>$</span>
              <Input
                className='pl-10'
                label='Yearly Income'
                id='wages'
                name='wages'
                type='number'
                value={income?.wages.value}
                error={income?.wages?.error}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
              />
            </div>
            <div className='relative'>
              <span className='absolute inset-y-12 left-1 flex items-center pointer-events-none'>$</span>
              <Input
                className='pl-10'
                label='Benefits/Pensions'
                id='pensions'
                name='pensions'
                type='number'
                value={income.pensions.value}
                error={income?.pensions?.error}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
              />
            </div>
            <div className='relative'>
              <span className='absolute inset-y-12 left-1 flex items-center pointer-events-none'>$</span>
              <Input
                className='pl-10'
                label='Public Assistance'
                id='public-assistance'
                name='publicAssistance'
                type='number'
                value={income?.publicAssistance.value}
                error={income?.publicAssistance?.error}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
              />
            </div>
            <div className='relative'>
              <span className='absolute inset-y-12 left-1 flex items-center pointer-events-none'>$</span>
              <Input
                className='pl-10'
                label='Other Income'
                id='other-income'
                name='otherIncome'
                type='number'
                value={income?.otherIncome.value}
                error={income?.otherIncome?.error}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
                onBlur={(e: ChangeEvent<HTMLInputElement>) => {
                  inputChangedHandler(e.target.value, e.target.name);
                }}
              />
            </div>
            <div className='col-span-6'>
              <FileInput
                id='principal-doc'
                name='principalDoc'
                type='file'
                label='Proof of income'
                multiple
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleFileChange(e);
                }}
              />
              {selectedFiles?.length > 0 && (
                <div className='flex flex-col border'>
                  {selectedFiles?.map((pp: any, index: number) => {
                    return (
                      <div key={index} className='flex justify-between px-4 my-1'>
                        <span>{pp.name}</span>
                        <span
                          className='flex justify-end text-neutralColor-light hover:text-neutralColor-normal cursor-pointer'
                          onClick={() => removeFileHandler(pp.name)}
                        >
                          <XCircleIcon className='h-5 w-5 ' />
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {uploadError && <InputError>{uploadError}</InputError>}
            </div>
            <div className='col-span-6'>
              <FileInput
                id='principal-doc'
                name='principalDoc'
                type='file'
                label='Bank statement'
                multiple
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  handleFileChange(e);
                }}
              />
              {selectedFiles?.length > 0 && (
                <div className='flex flex-col border'>
                  {selectedFiles?.map((pp: any, index: number) => {
                    return (
                      <div key={index} className='flex justify-between px-4 my-1'>
                        <span>{pp.name}</span>
                        <span
                          className='flex justify-end text-neutralColor-light hover:text-neutralColor-normal cursor-pointer'
                          onClick={() => removeFileHandler(pp.name)}
                        >
                          <XCircleIcon className='h-5 w-5 ' />
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              {uploadError && <InputError>{uploadError}</InputError>}
            </div>
          </div>
        </section>
        <div className='flex justify-end gap-4 mt-10'>
          <Button disabled={false} label='Cancel' type='button' btnStyle='neutral' onClick={modalHandler} />
          <Button
            label={`${isEditing ? 'Save' : 'Add'}`}
            type='button'
            btnStyle='primary'
            onClick={isEditing ? updateIncomeHandler : onAddIncomeHandler}
          />
        </div>
      </div>
    </Modal>
  );
}
